import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Wrench, ArrowLeft, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/utils2";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { applyAuthPayload } = useAuth();
  const navigate = useNavigate();
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (pwd.length < 6) { setError("Password must be at least 6 characters."); return; }
    if (pwd !== confirm) { setError("Passwords don't match."); return; }
    if (!token) { setError("This reset link is invalid. Please request a new one."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, new_password: pwd });
      applyAuthPayload({
        user_id: data.user_id,
        email: data.email,
        name: data.name,
        picture: data.picture,
        provider: data.provider,
        settings: data.settings,
        access_token: data.access_token,
      });
      navigate("/app");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 flex items-center justify-center p-6 noise-overlay">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-10">
          <div className="h-11 w-11 grid place-items-center bg-orange-600 rounded-sm">
            <Wrench className="h-6 w-6 text-white" strokeWidth={2.5} />
          </div>
          <div className="font-display font-black text-2xl tracking-tighter">
            MAINTAIN<span className="text-orange-500">HQ</span>
          </div>
        </div>

        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3">// SET NEW PASSWORD</div>
        <h2 className="font-display font-black text-3xl tracking-tight mb-3">Choose a new password.</h2>
        <p className="text-zinc-400 text-sm leading-relaxed mb-8">
          Set a password you'll remember. Minimum 6 characters — longer is better.
        </p>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <Label htmlFor="rp-pwd" className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">New password</Label>
            <Input
              id="rp-pwd"
              type="password"
              required
              minLength={6}
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              className="mt-1 bg-zinc-950 border-zinc-800 focus:border-orange-500 focus:ring-orange-500 rounded-sm"
              data-testid="reset-password-input"
            />
          </div>
          <div>
            <Label htmlFor="rp-confirm" className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Confirm password</Label>
            <Input
              id="rp-confirm"
              type="password"
              required
              minLength={6}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="mt-1 bg-zinc-950 border-zinc-800 focus:border-orange-500 focus:ring-orange-500 rounded-sm"
              data-testid="reset-confirm-input"
            />
          </div>
          {error ? (
            <div className="border border-red-900/50 bg-red-950/40 text-red-300 px-3 py-2 text-sm rounded-sm" data-testid="reset-error">
              {error}
            </div>
          ) : null}
          <Button
            type="submit"
            disabled={busy}
            className="w-full bg-orange-600 hover:bg-orange-500 text-white uppercase tracking-widest font-bold rounded-sm py-6"
            data-testid="reset-submit-button"
          >
            {busy ? "Saving..." : (<><CheckCircle2 className="w-4 h-4 mr-2" /> Save password & sign in</>)}
          </Button>
        </form>

        <div className="mt-8 text-center">
          <Link to="/login" className="inline-flex items-center gap-2 text-xs text-zinc-500 hover:text-orange-500 uppercase tracking-widest">
            <ArrowLeft className="w-3 h-3" /> Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
